import React from "react";
import styled from "@emotion/styled";
import {
  Box,
  Card as MuiCard,
  CardHeader,
  IconButton,
  Paper as MuiPaper,
  TablePagination,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { MoreVertical } from "react-feather";
import { spacing } from "@mui/system";

const Card = styled(MuiCard)(spacing);
const Paper = styled(MuiPaper)(spacing);

/**
 * DataGridTable - Advanced table component using MUI DataGrid
 *
 * @param {Object} props
 * @param {string} props.title - Table title
 * @param {string} props.subtitle - Table subtitle
 * @param {Array} props.columns - DataGrid column definitions
 * @param {Array} props.rows - DataGrid row data
 * @param {boolean} props.pagination - Enable pagination in the table
 * @param {number} props.pageSize - Number of rows per page
 * @param {Function} props.onPageChange - Callback for page change
 * @param {Function} props.onPageSizeChange - Callback for page size change
 * @param {boolean} props.showHeader - Show the card header
 * @param {React.Node} props.actions - Custom actions for the header
 * @param {Object} props.dataGridProps - Additional props for the DataGrid
 * @param {Object} props.cardProps - Additional props for the Card
 * @param {string} props.height - Fixed height for the DataGrid
 */
const DataGridTable = ({
  title,
  subtitle,
  columns = [],
  rows = [],
  pagination = true,
  pageSize = 5,
  onPageChange,
  onPageSizeChange,
  showHeader = true,
  actions,
  dataGridProps = {},
  cardProps = {},
  height = "400px",
}) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(pageSize);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setRowsPerPage(newPageSize);
    setPage(0);
    if (onPageSizeChange) {
      onPageSizeChange(newPageSize);
    }
  };

  // Default actions
  const defaultActions = (
    <IconButton aria-label="settings" size="large">
      <MoreVertical />
    </IconButton>
  );

  return (
    <Card mb={6} {...cardProps}>
      {showHeader && (
        <CardHeader
          action={actions || defaultActions}
          title={title || "Table"}
          subheader={subtitle}
        />
      )}
      <Paper>
        <Box sx={{ height, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            // Handle pagination based on props
            {...(pagination
              ? {
                  pagination: true,
                  paginationMode: "client",
                  rowsPerPageOptions: [5, 10, 25],
                  pageSize: rowsPerPage,
                  onPageSizeChange: handleChangeRowsPerPage,
                  page: page,
                  onPageChange: handleChangePage,
                }
              : {
                  pagination: false,
                  hideFooter: true, // Automatically hide footer when pagination is disabled
                })}
            disableSelectionOnClick
            autoHeight={!height}
            // Apply custom styles
            sx={{
              boxShadow: 0,
              border: 0,
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeader:focus": {
                outline: "none",
              },
            }}
            // Allow custom props to override defaults
            {...dataGridProps}
          />
        </Box>
      </Paper>
    </Card>
  );
};

export default DataGridTable;
